// The Traffic tab's generated flows (feature spec 17, phase 2; spec 11 6.15.21, spec 18 6.4): each attached deployment's
// blueprint connections from its apps to privately reachable resources, read from the blueprints of the attachments.
import { useQueries } from '@tanstack/react-query';
import { useApi } from '../../api/context';
import { queryKeys, useResourceTypes } from '../../api/queries';
import type { Attachment, BlueprintDocument, ResourceType } from '../../api/types';

export interface GeneratedFlow {
  deploymentId: string;
  deploymentName: string;
  region: string;
  sourceId: string;
  targetId: string;
  port: number | null;
}

const APP_TYPES = ['compute.webapp', 'compute.function'];

/** The port of a connection's target from its type's `requestPort`. */
export function portOf(
  type: ResourceType | undefined,
  properties: Record<string, unknown> | undefined,
): number | null {
  const rule = type?.requestPort;
  if (!rule) return null;
  if (rule.port !== null) return rule.port;
  const key = rule.property?.replace(/^\//, '') ?? '';
  const value = properties?.[key];
  return (typeof value === 'string' && rule.byValue?.[value]) || null;
}

export function flowsOf(
  attachments: readonly Attachment[],
  docs: ReadonlyMap<string, BlueprintDocument>,
  types: readonly ResourceType[],
): GeneratedFlow[] {
  const byType = new Map(types.map((t) => [t.type, t]));
  const flows: GeneratedFlow[] = [];
  for (const a of attachments) {
    const doc = docs.get(a.blueprintId);
    if (!doc) continue;
    for (const c of doc.connections ?? []) {
      if (c.type !== 'connects-to') continue;
      const source = doc.resources.find((r) => r.id === c.sourceId);
      const target = doc.resources.find((r) => r.id === c.targetId);
      if (!source || !target || !APP_TYPES.includes(source.type)) continue;
      const type = byType.get(target.type);
      if (!type?.privatelyReachable) continue;
      flows.push({
        deploymentId: a.deploymentId,
        deploymentName: a.deploymentName,
        region: a.region,
        sourceId: c.sourceId,
        targetId: c.targetId,
        port: portOf(type, target.properties as Record<string, unknown> | undefined),
      });
    }
  }
  return flows;
}

/** The generated flows of the network's attachments (one blueprint read per blueprint). */
export function useGeneratedFlows(
  attachments: readonly Attachment[] | undefined,
): GeneratedFlow[] | undefined {
  const api = useApi();
  const types = useResourceTypes();
  const ids = [...new Set((attachments ?? []).map((a) => a.blueprintId).filter(Boolean))];
  const blueprints = useQueries({
    queries: ids.map((id) => ({
      queryKey: queryKeys.blueprint(id),
      queryFn: () => api.getBlueprint(id),
      staleTime: 60_000,
    })),
  });
  if (!attachments || !types.data || blueprints.some((b) => b.isPending)) return undefined;
  const docs = new Map<string, BlueprintDocument>();
  for (const b of blueprints) {
    const blueprint = b.data?.data;
    if (blueprint) docs.set(blueprint.id, blueprint.content);
  }
  return flowsOf(attachments, docs, types.data);
}
